import React, { useMemo } from "react";
import type {
  DiffActivity,
  DiffPredecessorChange,
  FieldDelta,
} from "../../../models";
import { formatLagSuffix, predTypeLabel } from "../../../utils/p6";
import { getActiveColumns, formatValue } from "./columns";
import type { ColumnDef } from "./columns";
import "./DiffActivityTable.css";

interface Props {
  activities: DiffActivity[];
  canRevert?: boolean;
  reverting?: string | null;
  onRevertField?: (taskCode: string, field: string) => void;
  onRevertActivity?: (taskCode: string) => void;
}

interface DeltaCellProps {
  delta: FieldDelta | undefined;
  col: ColumnDef;
  canRevert?: boolean;
  isReverting?: boolean;
  onRevert?: () => void;
  /** Extra marker rendered after the value (e.g. the pushed-start anchor). */
  marker?: React.ReactNode;
}

const DeltaCell: React.FC<DeltaCellProps> = ({
  delta,
  col,
  canRevert,
  isReverting,
  onRevert,
  marker,
}) => {
  if (!delta) return <td>{marker}</td>;

  const fmtOld = formatValue(delta.old, col.format);
  const fmtNew = formatValue(delta.new, col.format);

  // Added row: only new value
  if (delta.old === null && delta.new !== null) {
    return (
      <td>
        <span className="delta-new-only">{fmtNew}</span>
        {marker}
      </td>
    );
  }

  // Removed row: only old value
  if (delta.old !== null && delta.new === null) {
    return (
      <td>
        <span className="delta-old-only">{fmtOld}</span>
        {marker}
      </td>
    );
  }

  // Changed: old → new
  return (
    <td>
      <span className="delta-old">{fmtOld}</span>
      <span className="delta-arrow">{"\u2192"}</span>
      <span className="delta-new">{fmtNew}</span>
      {marker}
      {canRevert && onRevert && (
        <button
          className="delta-revert-btn"
          onClick={onRevert}
          disabled={isReverting}
          title="Revert to original"
        >
          ↩
        </button>
      )}
    </td>
  );
};

const DiffActivityTable: React.FC<Props> = ({ activities, canRevert, reverting, onRevertField, onRevertActivity }) => {
  const columns = useMemo(() => getActiveColumns(activities), [activities]);

  return (
    <table className="diff-activity-table">
      <thead>
        <tr>
          <th>Code</th>
          <th>Name</th>
          {columns.map((col) => (
            <th key={col.key}>{col.label}</th>
          ))}
          {canRevert && <th></th>}
        </tr>
      </thead>
      <tbody>
        {activities.map((act) => {
          const isActivityReverting = reverting === act.task_code;
          const showRevert = canRevert && act.type === "changed";
          const predChanges = act.predecessor_changes ?? [];
          const totalCols = 2 + columns.length + (canRevert ? 1 : 0);
          // A pushed start writes a Start-On-or-After alongside the dates —
          // surfaced as an anchor on the Restart delta, not as field rows
          // (cstr_* are deliberately absent from MAIN_COLUMNS).
          const cstrHeldDate = act.changes["cstr_type"]?.new === "CS_MSOA"
            ? (act.changes["cstr_date"]?.new ?? null)
            : null;
          return (
            <React.Fragment key={act.task_code}>
              <tr className={`diff-activity-table__row--${act.type}`}>
                <td className="diff-activity-table__code">{act.task_code}</td>
                <td className="diff-activity-table__name" title={act.task_name}>
                  {act.task_name}
                </td>
                {columns.map((col) => {
                  const delta = act.changes[col.key];
                  const isFieldRevertable = showRevert && delta && delta.old !== null && delta.new !== null;
                  const marker = col.key === "restart_date" && cstrHeldDate ? (
                    <span
                      className="diff-cstr-icon"
                      title={`Held by a pushed Start On or After (${formatValue(cstrHeldDate, "date")}) — survives reschedules and exports to P6`}
                    >
                      ⚓
                    </span>
                  ) : undefined;
                  return (
                    <DeltaCell
                      key={col.key}
                      delta={delta}
                      col={col}
                      marker={marker}
                      canRevert={isFieldRevertable}
                      isReverting={reverting === `${act.task_code}:${col.key}`}
                      onRevert={isFieldRevertable && onRevertField ? () => onRevertField(act.task_code, col.key) : undefined}
                    />
                  );
                })}
                {canRevert && (
                  <td>
                    {showRevert && onRevertActivity && (
                      <button
                        className="diff-revert-all-btn"
                        onClick={() => onRevertActivity(act.task_code)}
                        disabled={isActivityReverting || reverting != null}
                        title="Revert all fields to original"
                      >
                        {isActivityReverting ? "..." : "Revert"}
                      </button>
                    )}
                  </td>
                )}
              </tr>
              {predChanges.length > 0 && (
                <tr className="diff-activity-table__pred-row">
                  <td colSpan={totalCols}>
                    <PredChangeList changes={predChanges} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

const PredChangeList: React.FC<{ changes: DiffPredecessorChange[] }> = ({ changes }) => (
  <div className="diff-pred-changes">
    <span className="diff-pred-changes__label">Predecessors:</span>
    {changes.map((pc, i) => (
      <PredChangeBadge key={`${pc.pred_activity_id}-${i}`} change={pc} />
    ))}
  </div>
);

const PredChangeBadge: React.FC<{ change: DiffPredecessorChange }> = ({ change }) => {
  if (change.kind === "added") {
    return (
      <span className="diff-pred-badge added" title="Added predecessor">
        + {change.pred_task_code} ({predTypeLabel(change.new_pred_type ?? "")}
        {formatLagSuffix(change.new_lag_hours ?? 0)})
      </span>
    );
  }
  if (change.kind === "removed") {
    return (
      <span className="diff-pred-badge removed" title="Removed predecessor">
        − {change.pred_task_code} ({predTypeLabel(change.old_pred_type ?? "")}
        {formatLagSuffix(change.old_lag_hours ?? 0)})
      </span>
    );
  }
  // changed
  return (
    <span className="diff-pred-badge changed" title="Changed predecessor lag/type">
      {change.pred_task_code}: {predTypeLabel(change.old_pred_type ?? "")}
      {formatLagSuffix(change.old_lag_hours ?? 0)}
      {" → "}
      {predTypeLabel(change.new_pred_type ?? "")}
      {formatLagSuffix(change.new_lag_hours ?? 0)}
    </span>
  );
};

export default DiffActivityTable;
